import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { Row } from "@faire/web--source/slate/Layout";
import { TypographyStyles } from "@faire/web--source/slate/Typography";
import { useCallbackExperiment } from "@million/lint/runtime";
import React, { useRef, useEffect, useCallback } from "react";
import styled from "styled-components";
var RETURN_KEY = 13;
var ESCAPE_KEY = 27;
var KEY_EVENT_VALUE = "keydown";
/**
 * A generic search input text component that can be used with different underlying input types,
 * such as `input` and `textarea`.
 *
 * The underlying input type component is passed as a `SearchInputInnerComponent`.
 */ export var SearchInputBase = function(param) {
    var SearchInputInnerComponent = param.SearchInputInnerComponent, searchValue = param.searchValue, onClick = param.onClick, onSearchChange = param.onSearchChange, onFocus = param.onFocus, onReturnPressed = param.onReturnPressed, onEscapePressed = param.onEscapePressed, onHover = param.onHover, placeholder = param.placeholder, id = param.id, getRef = param.getRef, inputDataTestId = param.inputDataTestId, ariaLabel = param["aria-label"], _param_isTOPCRSCExperimentEnabled = param.isTOPCRSCExperimentEnabled, isTOPCRSCExperimentEnabled = _param_isTOPCRSCExperimentEnabled === void 0 ? false : _param_isTOPCRSCExperimentEnabled;
    var handleKeyDown = useCallbackExperiment(function(event) {
        if (event.type !== KEY_EVENT_VALUE || ![
            RETURN_KEY,
            "Enter",
            ESCAPE_KEY,
            "Escape"
        ].includes(event.keyCode || event.key)) {
            return;
        }
        event.preventDefault();
        switch(event.keyCode || event.key){
            case RETURN_KEY:
            case "Enter":
                onReturnPressed();
                break;
            case ESCAPE_KEY:
            case "Escape":
                onEscapePressed();
                break;
            default:
                break;
        }
    }, [
        onEscapePressed,
        onReturnPressed
    ], isTOPCRSCExperimentEnabled);
    // If an input component with the autoComplete value 'username' is not provided, Chrome's autofill will just fill the first input field it finds.
    // The working solution is to provide a hidden username field to guard against any unwanted values.
    var hiddenInputField = /*#__PURE__*/ _jsx("input", {
        name: "auto-fill guard",
        type: "username",
        style: {
            display: "none"
        },
        autoComplete: "username"
    });
    var inputRef = useRef(null);
    var isMounted = useRef(false);
    // Handle case where user has started to enter text during SSR by updating state
    // on hydration.
    useEffect(function() {
        var _inputRef_current;
        if (isMounted.current || !inputRef.current) {
            return;
        }
        isMounted.current = true;
        var domValue = (_inputRef_current = inputRef.current) === null || _inputRef_current === void 0 ? void 0 : _inputRef_current.value;
        if (domValue) {
            onSearchChange(domValue);
        }
    }, [
        onSearchChange
    ]);
    var handleSearchChange = useCallback(function(ev) {
        onSearchChange(ev.target.value, ev.timeStamp);
    }, [
        onSearchChange
    ]);
    return /*#__PURE__*/ _jsxs(Wrapper, {
        onClick: onClick,
        onMouseOver: onHover,
        children: [
            hiddenInputField,
            /*#__PURE__*/ _jsx(SearchInputInnerComponent, {
                id: id,
                value: searchValue,
                onChange: handleSearchChange,
                onFocus: onFocus,
                onKeyDown: handleKeyDown,
                placeholder: placeholder,
                "aria-label": ariaLabel,
                ref: function(ref) {
                    getRef(ref);
                    inputRef.current = ref;
                },
                autoComplete: "off",
                "data-test-id": inputDataTestId
            })
        ]
    });
};
/**
 * A search input component that uses an `input` element as the underlying input type.
 */ export var SearchInput = function(props) {
    return /*#__PURE__*/ _jsx(SearchInputBase, _object_spread_props(_object_spread({}, props), {
        SearchInputInnerComponent: StyledInput
    }));
};
var Wrapper = styled(Row).withConfig({
    componentId: "sc-6a806a2a-0"
})([
    "position:relative;width:100%;"
]);
/**
 * @trackcomponent
 * @supersededBy "@faire/web/slate/SearchInput/SearchInput#SearchInput"
 */ var StyledInput = styled.input.withConfig({
    componentId: "sc-6a806a2a-1"
})([
    "",
    " padding-left:20px;background-color:unset;width:100%;border:0;&::placeholder{color:",
    ";}"
], TypographyStyles({
    as: undefined
}), Core.text.subdued);
